import { createRouter, createWebHistory } from 'vue-router'
import { toast } from 'vue-sonner'
// LAYOUTS
import LayoutBoxDetail from '@/layouts/LayoutBoxDetail.vue'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import LayoutEmpty from '@/layouts/LayoutEmpty.vue'
import LayoutError from '@/layouts/LayoutError.vue'
import LayoutFilePreview from '@/layouts/LayoutFilePreview.vue'
import LayoutStore from '@/layouts/LayoutStore.vue'
import DeepErrorHandlerService from '@/services/DeepErrorHandlerService'
import { captureException } from '@sentry/browser'
import { useLoader } from '@deepcloud/deep-ui-lib'
import { useDeepAdminUsersMeStore } from '@/stores/deepadmin/users/users-me'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core'
import { useApi } from '@/composables/api'
// SERVICES
import { useKeycloak } from '@/composables/auth/keycloak'
import middlewareDeepMail from './middleware/deep-mail'
import needsOrganizationContext from './middleware/needs-organization-context'
// MIDDLEWARES
import routeNotFound from './middleware/route-not-found'
// ROUTES
import linkRoutes from './routes/link'
import uploadRoutes from './routes/upload'
import watchesRoutes from './routes/watches'
import type { KeycloakLoginOptions } from 'keycloak-js'
import type { RouteLocationNormalized } from 'vue-router'

const routes = [
  {
    path: '/:catchAll(.*)*',
    alias: '/error-page',
    name: 'error-page',
    component: LayoutError,
    meta: {
      auth: false,
    },
    props: {
      error: {
        statusCode: 404,
        title: 'error.page_not_found',
        deepBoxError: true,
      },
    },
    beforeEnter: (to: RouteLocationNormalized) =>
      routeNotFound({
        to,
      }),
  },
  // ORGANIZATION
  {
    path: '/:organization?',
    meta: {
      needsOrganizationContext: true,
    },
    children: [
      {
        path: '',
        component: LayoutDefault,
        children: [
          {
            path: '',
            name: 'organization',
            component: () =>
              import('@/pages/organization/OrganizationDashboard.vue'),
          },
          {
            path: 'types/:type',
            component: () =>
              import('@/pages/organization/type/OrganizationType.vue'),
            name: 'organization-type',
            meta: {
              needsOrganizationContext: true,
            },
          },
          {
            path: 'deepmail',
            name: 'deepmail',
            component: () =>
              import('@/pages/organization/deepmail/DeepMailView.vue'),
          },
        ],
      },
      {
        path: 'store',
        component: LayoutStore,
        children: [
          {
            path: 'services/:service',
            component: () =>
              import('@/pages/organization/store/service/StoreServiceIndex.vue'),
            name: 'organization-store-services-service',
            props: true,
          },
          {
            path: 'services/:service?/process',
            component: () =>
              import('@/pages/organization/store/service/StoreServiceProcess.vue'),
            name: 'organization-store-services-service-process',
          },
        ],
      },
    ],
  },
  // ORGANIZATION FILE PREVIEW
  {
    path: '/node/:node/preview',
    component: LayoutFilePreview,
    meta: {
      needsOrganizationContext: true,
    },
    children: [
      {
        path: '',
        name: 'node-preview',
        component: () => import('@/pages/node/NodePreview.vue'),
      },
    ],
  },
  // OO NODE EDITOR
  {
    path: '/node/:node/edit',
    component: LayoutEmpty,
    children: [
      {
        path: '',
        name: 'node-edit',
        component: () => import('@/pages/node/NodeEdit.vue'),
      },
      {
        path: 'cloudoffice',
        name: 'node-edit-cloudoffice',
        component: () => import('@/pages/node/NodeEditCloudOffice.vue'),
      },
    ],
  },

  // BOX DETAIL
  {
    path: '/types/:type/boxes/:box',
    component: LayoutBoxDetail,
    meta: {
      needsOrganizationContext: true,
    },
    beforeEnter: async (to: RouteLocationNormalized) => {
      const { type, box } = to.params
      const loader = useLoader()
      const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
      const { deepOAPI } = useApi()

      try {
        loader.show()
        await deepBoxBoxInfoStore.fetchBoxInfo(box as string)
      } catch {
        // error already recorded by fetchBoxInfo; keep the route so it displays in place
        return
      } finally {
        loader.hide()
      }

      if (!deepBoxBoxInfoStore.boxInfo) {
        // navigate to dashboard as fallback
        return { path: '/' }
      }

      const deepBoxCoreStore = useDeepBoxCoreStore()
      deepBoxCoreStore.selectedBox = { ...deepBoxBoxInfoStore.boxInfo }

      const organization = deepBoxBoxInfoStore.boxInfo?.company?.companyId

      const shouldRedirectToPortal =
        deepBoxBoxInfoStore.boxInfo.boxType === 'portal' &&
        !!organization &&
        !useDeepAdminUsersMeStore().getOrganizationById(organization)

      if (shouldRedirectToPortal) {
        try {
          const { data } = await deepOAPI.value.portalMappingsBoxes.get(
            organization,
            type as string,
            box as string,
          )
          if (data && data.portalUrl) {
            // cancel current navigation

            window.open(data.portalUrl, '_self')
            return
          }
        } catch {
          // abort navigation and show error
          toast.error('DeepPortal API failed')
          return // Navigate to a fallback route
        }
      }
      return
      // Continue navigation for non-local environments
    },
    children: [
      {
        path: '',
        redirect: { name: 'organization-types-type-boxes-box' }, // Redirect to BoxIndex
      },
      {
        path: '',
        name: 'organization-types-type-boxes-box',
        component: () => import('@/pages/organization/type/box/BoxIndex.vue'),
      },
      {
        path: '',
        component: () => import('@/pages/organization/type/box/BoxLayout.vue'),
        children: [
          {
            path: 'files',
            component: () =>
              import('@/pages/organization/type/box/BoxFolderView.vue'),
            name: 'organization-types-type-boxes-box-files',
          },
          {
            path: 'files/:node',
            component: () =>
              import('@/pages/organization/type/box/BoxFolderView.vue'),
            name: 'organization-types-type-boxes-box-files-node',
          },
          {
            path: 'inbox',
            component: () =>
              import('@/pages/organization/type/box/BoxFolderView.vue'),
            name: 'organization-types-type-boxes-box-inbox',
          },
          {
            path: 'search',
            component: () =>
              import('@/pages/organization/type/box/BoxSearch.vue'),
            name: 'organization-types-type-boxes-box-search',
            props: true,
          },
          {
            path: 'trash',
            component: () =>
              import('@/pages/organization/type/box/BoxFolderView.vue'),
            name: 'organization-types-type-boxes-box-trash',
          },
          {
            path: 'trash/:node',
            component: () =>
              import('@/pages/organization/type/box/BoxFolderView.vue'),
            name: 'organization-types-type-boxes-box-trash-node',
          },
        ],
      },
    ],
  },
  // LINK
  ...linkRoutes,
  // WATCHES
  ...watchesRoutes,
  // UPLOAD
  ...uploadRoutes,
]

// Create fn to init the router in order to solve an issue where KC query params stay on the URL
// https://github.com/dsb-norge/vue-keycloak-js/issues/94#issuecomment-1794403391
// https://github.com/keycloak/keycloak/issues/14742#issuecomment-1663069438
const initializeRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
  })

  router.beforeEach(async (to) => {
    // ############ CLEAR ERROR IF EXISTS #########
    DeepErrorHandlerService.clearErrorIfExists()

    const routeLangQuery = (to.query?.lang as string) || ''

    const basePath = window.location.origin
    const keycloakRedirectUri = basePath + to.fullPath
    const toRouteNeedsAuth = to.meta.auth !== false
    const { isAuthenticated, login } = useKeycloak()

    const params: KeycloakLoginOptions = {
      redirectUri: keycloakRedirectUri,
    }
    if (routeLangQuery) {
      params.locale = routeLangQuery
    }
    const queryEmail = to.query.email as string
    const queryMailRef = to.query?.['mail-ref'] as string

    if (isAuthenticated.value) {
      const deepAdminUsersMeStore = useDeepAdminUsersMeStore()
      if (!deepAdminUsersMeStore.user) {
        await deepAdminUsersMeStore.fetchUserOrganizations()
      }
    }
    if (toRouteNeedsAuth) {
      if (queryEmail && queryMailRef) {
        return middlewareDeepMail({ to, params })
      }
      if (!isAuthenticated.value) {
        console.log('Route need auth but user is not authenticated')

        return login(params)
      } else {
        const paramNeedsOrganizationContext = to.matched.some(
          (m) => m.meta.needsOrganizationContext,
        )
        if (paramNeedsOrganizationContext) {
          return needsOrganizationContext({ to })
        }
      }
    }
  })

  // Hard-reload the page when chunk load errors match the navigation error
  // https://github.com/vitejs/vite/issues/11804#issuecomment-1760951463
  router.onError((error, to) => {
    const errors = [
      'Failed to fetch dynamically imported module',
      'Unable to preload CSS',
      'error loading dynamically imported module',
    ]

    if (errors.some((e) => error?.message?.includes(e))) {
      if (to?.fullPath) {
        window.location.href = to.fullPath
      } else {
        window.location.reload()
      }
    } else {
      captureException('Triggered automatic reload after router error. ', {
        extra: error.message,
      })
    }
  })

  console.log('ROUTER: router initialized')

  return router
}

export default initializeRouter
